<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Clients" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            v-if="!isReadOnlyAccess"
            icon="add"
            color="primary"
            text="Add"
            :routeName="clientCreateRoute.name"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <ClientFilters
        class="px-5 mb-sm-5"
        :clientNames="clientNames"
        @search="handleSearchClick"
        @clear="handleSearchClick"
      ></ClientFilters>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="clientList"
          :options.sync="options"
          :server-items-length="serverItemsLength"
          item-key="id"
          class="elevation-2"
        >
          <template v-slot:item.active="{ item }">
            <v-simple-checkbox
              v-model="item.active"
              disabled
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              v-if="!isReadOnlyAccess"
              class="mx-1 my-1"
              color="primary"
              outlined
              @click.prevent="editClient($event, item)"
            >
              <v-icon color="primary">edit</v-icon>
            </v-btn>
            <v-btn
              v-if="isReadOnlyAccess"
              class="mx-1 my-1"
              color="primary"
              outlined
              @click.prevent="editClient($event, item)"
            >
              <v-icon color="primary">visibility</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { clientCreateRoute, clientEditRoute } from '@/lib/constants/routes'
import ClientFilters from './ClientFilters'
import { BottomSpacer, ButtonCustomIcon } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { userHasRoles, getCurrentUser } from '@/lib/authentication/index'
import { ADMIN, SALES_PERSON, MANAGER } from '@/lib/constants/securityRoles'
import { parseGridOptionsFromQuery, buildPaginationQueryParams, isAllRecordsSortOnlyChange, setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'

export default {
  components: {
    ClientFilters,
    BottomSpacer,
    ButtonCustomIcon,
    SimplePageHeader
  },
  data () {
    return {
      clientList: [],
      clientCreateRoute,
      headers: [
        { text: 'Business Entity', value: 'businessEntityName', width: '20%' },
        { text: 'Name', value: 'name', width: '25%' },
        { text: 'Invoice Frequency', value: 'invoiceFrequencyName', width: '18%' },
        { text: 'Third Party Portal', value: 'thirdPartyPortalName', width: '17%' },
        { text: 'Active', value: 'active', align: 'center', class: 'text-center', width: '10%', sortable: false },
        { text: 'Actions', value: 'action', align: 'center', class: 'text-xs-right', width: '10%', sortable: false }
      ],
      clientNames: [],
      hasFetchedAllClients: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['name'],
        sortDesc: [false]
      }
    }
  },
  computed: {
    ...mapFields('clients', [
      'totalRecords'
    ]),
    ...mapFields('clientFilters', [
      'businessEntityId',
      'name',
      'active',
      'invoiceFrequencyId',
      'thirdPartyPortalId'
    ]),
    isReadOnlyAccess () {
      return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER, SALES_PERSON] })
    },
    serverItemsLength () {
      return this.options.itemsPerPage === -1 ? undefined : this.totalRecords
    }
  },
  watch: {
    options: {
      handler (newOptions, previousOptions) {
        this.syncOptionsToQuery()

        if (!this.shouldFetchClients(newOptions, previousOptions)) {
          return
        }

        this.fetchClients()
      },
      deep: true
    }
  },
  created () {
    this.applyFiltersFromQuery()
    this.options = {
      ...this.options,
      ...this.getOptionsFromQuery()
    }
    this.syncOptionsToQuery()

    this.getClients().then(clients => {
      this.clientNames = clients
        .map(client => ({ clientName: client.name }))
        .sort((a, b) => (a.clientName > b.clientName) ? 1 : -1)
    })
  },
  methods: {
    ...mapActions('clients', [
      'getClientsWithFiltersPaginated',
      'getFieldsWithFilters',
      'getClients'
    ]),
    ...mapActions('clientFilters', [
      'clearFilters'
    ]),
    userHasRoles,
    getCurrentUser,
    editClient (event, client) {
      if (event.ctrlKey === true) {
        let route = this.$router.resolve({ name: clientEditRoute.name, params: { id: client.id } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: clientEditRoute.name, params: { id: client.id } })
      }
    },
    getOptionsFromQuery () {
      return parseGridOptionsFromQuery(this.$route.query, this.options)
    },
    applyFiltersFromQuery () {
      const { businessEntityId, name, active, invoiceFrequencyId, thirdPartyPortalId } = this.$route.query

      this.businessEntityId = businessEntityId ? Number(businessEntityId) : null
      this.name = typeof name === 'string' && name.length ? name : null
      this.invoiceFrequencyId = invoiceFrequencyId ? Number(invoiceFrequencyId) : null
      this.thirdPartyPortalId = thirdPartyPortalId ? Number(thirdPartyPortalId) : null

      if (active === 'true') {
        this.active = true
      } else if (active === 'null') {
        this.active = null
      } else {
        this.active = true
      }
    },
    syncOptionsToQuery () {
      const { sortColumn, sortDirection, ...paginationParams } = buildPaginationQueryParams(this.options)

      const nextQuery = { ...this.$route.query, ...paginationParams }

      setOrDeleteQueryParam(nextQuery, 'sortColumn', sortColumn)
      setOrDeleteQueryParam(nextQuery, 'sortDirection', sortColumn ? sortDirection : null)

      setOrDeleteQueryParam(nextQuery, 'businessEntityId', this.businessEntityId)
      setOrDeleteQueryParam(nextQuery, 'name', this.name)
      setOrDeleteQueryParam(nextQuery, 'invoiceFrequencyId', this.invoiceFrequencyId)
      setOrDeleteQueryParam(nextQuery, 'thirdPartyPortalId', this.thirdPartyPortalId)
      if (this.active === null) {
        nextQuery.active = 'null'
      } else {
        setOrDeleteQueryParam(nextQuery, 'active', this.active)
      }

      if (hasQueryChanged(this.$route.query, nextQuery, ['pageNumber', 'pageSize', 'sortColumn', 'sortDirection', 'businessEntityId', 'name', 'active', 'invoiceFrequencyId', 'thirdPartyPortalId'])) {
        this.$router.replace({ query: nextQuery })
      }
    },
    fetchClients () {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options

      if (itemsPerPage === -1) {
        this.getFieldsWithFilters().then(clients => {
          this.hasFetchedAllClients = true
          this.clientList = clients
        })
        return
      }

      this.hasFetchedAllClients = false
      this.getClientsWithFiltersPaginated({
        pageNumber: page,
        pageSize: itemsPerPage,
        sortColumn: sortBy && sortBy.length ? sortBy[0] : null,
        sortDirection: sortDesc && sortDesc.length ? (sortDesc[0] ? 'DESC' : 'ASC') : null
      }).then(clients => {
        this.clientList = clients
      })
    },
    shouldFetchClients (newOptions, previousOptions) {
      return !isAllRecordsSortOnlyChange(newOptions, previousOptions, this.hasFetchedAllClients)
    },
    handleSearchClick () {
      this.options = { ...this.options, page: 1 }
    }
  }
}
</script>
